import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages, features, steps } from '../data/mock';
import { ArrowRight, Truck, RotateCcw, ShieldCheck, Sparkles, Check } from 'lucide-react';

const featureIcons = [Sparkles, Truck, RotateCcw, ShieldCheck];

const HomePage = () => {
  useEffect(() => {
    document.title = 'Horizon Techline | Everyday Tech, Thoughtfully Chosen';
  }, []);

  const featured = products.slice(0, 8);

  return (
    <div className="min-h-screen bg-white text-dark-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-dark-50/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-dark-100 text-athletic-700 text-xs font-medium tracking-wide">
                  <Sparkles className="w-3.5 h-3.5" /> A calmer way to shop tech
                </span>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-dark-900 leading-[1.05]">
                  Everyday tech,<br />
                  <span className="text-athletic-600">thoughtfully chosen</span>
                </h1>
                <p className="mt-5 text-lg text-dark-500 max-w-md leading-relaxed">
                  A tightly curated shop for audio, gaming and creator gear &mdash; no clutter, no pressure. Just quiet quality, shipped free across the USA.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-athletic-600 text-white font-medium hover:bg-athletic-700 transition-colors">
                    Shop all products <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/about" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-dark-200 text-dark-700 font-medium hover:border-athletic-400 hover:text-athletic-700 transition-colors">
                    Our story
                  </Link>
                </div>
                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-dark-500">
                  {['Free USA shipping', '2\u20134 day delivery', '30-day returns'].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-athletic-600" /> {t}</span>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-dark-100 shadow-soft">
                  <img src={siteImages.heroMain} alt="Modern tech workspace" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl border border-dark-100 shadow-soft px-5 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-athletic-50 text-athletic-700 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
                  <div>
                    <p className="text-sm font-semibold text-dark-900">Secure & simple</p>
                    <p className="text-xs text-dark-400">Encrypted checkout, honest pricing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature strip */}
        <section className="border-y border-dark-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[i % featureIcons.length];
              return (
                <div key={f.id} className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-athletic-50 text-athletic-700 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
                  <div>
                    <p className="font-semibold text-dark-900 text-sm">{f.title}</p>
                    <p className="text-dark-500 text-sm leading-relaxed">{f.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-athletic-600 text-sm font-medium uppercase tracking-wider mb-2">Browse by category</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 tracking-tight">Find your kind of tech</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-dark-600 hover:text-athletic-700">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="group relative rounded-2xl overflow-hidden border border-dark-100 bg-dark-50">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/75 via-dark-900/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-white font-semibold text-lg">{cat.name}</h3>
                  <p className="text-white/70 text-xs mt-0.5">{cat.productCount} item{cat.productCount !== 1 ? 's' : ''}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured products */}
        <section className="bg-dark-50/70 border-y border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-athletic-600 text-sm font-medium uppercase tracking-wider mb-2">Popular right now</p>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 tracking-tight">Loved by our customers</h2>
              </div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-dark-600 hover:text-athletic-700">
                Shop all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>

        {/* Promo split */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center rounded-3xl border border-dark-100 overflow-hidden bg-white">
            <div className="aspect-[4/3] lg:aspect-auto lg:h-full">
              <img src={siteImages.heroSecondary} alt="Premium audio" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 lg:p-12">
              <p className="text-athletic-600 text-sm font-medium uppercase tracking-wider mb-2">Why Horizon</p>
              <h2 className="font-display text-3xl font-semibold text-dark-900 tracking-tight mb-4">Quality you can feel, prices you can trust</h2>
              <p className="text-dark-500 leading-relaxed mb-6">We keep the range small on purpose. Every gadget earns its place &mdash; tested for real-world clarity, comfort and reliability, then priced fairly. No gimmicks, just tech that works.</p>
              <ul className="space-y-3">
                {['Handpicked, no filler', 'Clear specs and honest descriptions', 'Friendly US-based support'].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-dark-700"><span className="w-6 h-6 rounded-full bg-athletic-50 text-athletic-700 flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5" /></span>{t}</li>
                ))}
              </ul>
              <Link to="/shop" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-dark-900 text-white font-medium hover:bg-athletic-600 transition-colors">
                Explore the shop <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="bg-dark-50/70 border-y border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="text-center mb-10">
              <p className="text-athletic-600 text-sm font-medium uppercase tracking-wider mb-2">How it works</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 tracking-tight">Simple, from browse to unbox</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((s, i) => (
                <div key={s.id} className="bg-white rounded-2xl border border-dark-100 p-6">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-athletic-600 text-white font-semibold">{i + 1}</span>
                  <h3 className="mt-4 font-semibold text-dark-900">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-dark-500 leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden">
          <img src={siteImages.ctaBackground} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-dark-900/80" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">Ready to upgrade the calm way?</h2>
            <p className="mt-4 text-dark-300 max-w-xl mx-auto">Browse the full collection and enjoy free, fast USPS shipping on every order.</p>
            <Link to="/shop" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-athletic-600 text-white font-medium hover:bg-athletic-500 transition-colors">
              Start shopping <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
